import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, tokenStore } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out, obj=logged in
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      tokenStore.set(data.access_token, data.refresh_token);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (email, password, name) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      tokenStore.set(data.access_token, data.refresh_token);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      console.warn("logout failed", _e);
    }
    tokenStore.clear();
    setUser(false);
  };

  const setCurrency = async (currency) => {
    const { data } = await api.patch("/auth/currency", { currency });
    setUser((u) => (u ? { ...u, currency: data.currency } : u));
  };

  return (
    <AuthCtx.Provider value={{ user, error, login, register, logout, setCurrency, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
